import { useEffect, useMemo, type MutableRefObject } from "react";
import { isQuickCheckoutVariant, type TurboPaymentMethod, type TurboVariantProps } from "@swiggy-private/payment-ui";

export type TurboPaymentSessionRefs = {
    hasCompletedInitialPaymentsLoadRef: MutableRefObject<boolean>;
    lastResolvedPaymentMethodRef: MutableRefObject<TurboPaymentMethod | null>;
};

type UseTurboPaymentSessionStateParams = TurboPaymentSessionRefs & {
    loading: boolean;
    turboPaymentMethod: TurboPaymentMethod | null | undefined;
    variant: TurboVariantProps["variant"];
};

type UseTurboPaymentSessionStateResult = {
    isLoadingForTheFirstTime: boolean;
    isQuickCheckout: boolean;
    resolvedPaymentMethod: TurboPaymentMethod | null | undefined;
};

/**
 * Session-scoped turbo payment UI state that must survive `ShowPayments` remounts.
 * The refs are owned by the wrapper above `ShowPayments` and passed in here.
 */
export const useTurboPaymentSessionState = ({
    loading,
    turboPaymentMethod,
    variant,
    hasCompletedInitialPaymentsLoadRef,
    lastResolvedPaymentMethodRef,
}: UseTurboPaymentSessionStateParams): UseTurboPaymentSessionStateResult => {
    useEffect(() => {
        if (!loading) {
            hasCompletedInitialPaymentsLoadRef.current = true;
        }
    }, [loading, hasCompletedInitialPaymentsLoadRef]);

    // Only the very first payments fetch of the session shows a shimmer; the refetches that
    // follow a cart update keep the widget on screen with an inline loading state instead.
    const isLoadingForTheFirstTime = loading && !hasCompletedInitialPaymentsLoadRef.current;

    useEffect(() => {
        if (turboPaymentMethod) {
            lastResolvedPaymentMethodRef.current = turboPaymentMethod;
        }
    }, [turboPaymentMethod, lastResolvedPaymentMethodRef]);

    const isQuickCheckout = isQuickCheckoutVariant(variant);

    // A payments fetch clears the resolved method, which would unmount the widget mid-update.
    const resolvedPaymentMethod = useMemo(
        () => (isQuickCheckout ? (turboPaymentMethod ?? lastResolvedPaymentMethodRef.current) : turboPaymentMethod),
        [isQuickCheckout, turboPaymentMethod, lastResolvedPaymentMethodRef],
    );

    return {
        isLoadingForTheFirstTime,
        isQuickCheckout,
        resolvedPaymentMethod,
    };
};
